import { useMutation, useQuery } from "@apollo/client";
import { clsx, Modal } from "@mantine/core";
import { MessageType } from "components/forms/message-view";
import config from "config";
import PublisherPill from "../../AllBusiness/components/PublisherPill";
import { useFormik } from "formik";
import {
  BrokerTypes,
  MockOffer,
  MockOfferDetail,
  MockOfferPublishedStruct,
} from "graphql/__generated__/globalTypes";
import {
  acceptOffer,
  readTransactionThread,
  sendOfferParticipationMessage,
} from "graphql/mutations/publish-offer";
import { fetchOfferParticipationTransactionThread } from "graphql/queries/publish-offers";
import { useRealtimeConnection } from "hooks/useRealTimeConnection";
import { EnhancedMessageView } from "pages/AllBusiness/components/EnhancedMessages";
import { FC, useEffect, useRef, useState } from "react";
import "react-quill/dist/quill.snow.css";

interface MessageFormValues {
  message: string;
}

interface ThreadContainerProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  offer?: MockOffer;
  offerDetail?: MockOfferDetail;
  offerPublished?: MockOfferPublishedStruct;
  participationId?: string;
  unreadMessageIds?: string[];
  refetch?: () => void;
  brokerName: BrokerTypes;
}

const ThreadContainer: FC<ThreadContainerProps> = ({
  open,
  setOpen,
  offer,
  offerDetail,
  offerPublished,
  participationId,
  unreadMessageIds = [],
  refetch,
  brokerName,
}) => {
  const [messages, setMessages] = useState<MessageType[]>([]);
  const hasMarkedAsRead = useRef(false);

  const offerPublishedId = offerPublished?.offer_published_id;
  const isParticipationAccepted =
    offerPublished?.offer_published_participant?.participation_status ===
    "ACCEPTED";

  const {
    data,
    loading: fetchLoading,
    refetch: refetchMessages,
  } = useQuery(fetchOfferParticipationTransactionThread, {
    variables: {
      offer_published_participant: participationId,
      published_by: brokerName || "",
    },
    skip: !participationId || !open,
    fetchPolicy: "network-only",
  });

  const [sendMessage, { loading: sendLoading }] = useMutation(
    sendOfferParticipationMessage
  );

  const [acceptOfferMutation, { loading: acceptLoading }] =
    useMutation(acceptOffer);

  const [markAsRead] = useMutation(readTransactionThread);

  useEffect(() => {
    if (data?.fetchOfferParticipationTransactionThread) {
      setMessages(data.fetchOfferParticipationTransactionThread);
    }
  }, [data]);

  useEffect(() => {
    if (open && unreadMessageIds && unreadMessageIds.length > 0) {
      markUnreadMessagesAsRead(unreadMessageIds);
    }
  }, [open, unreadMessageIds]);

  const markUnreadMessagesAsRead = (messageIds: string[]) => {
    if (!open || messageIds.length === 0) return;

    markAsRead({
      variables: {
        input: messageIds,
        published_by: brokerName || "",
      },
    })
      .then(() => {
        hasMarkedAsRead.current = true;
      })
      .catch((error) => {
        console.error("Error marking messages as read:", error);
      });
  };

  const handleMessageSubmission = async (message: string) => {
    if (participationId) {
      return await sendMessage({
        variables: {
          input: {
            offer_published_participant: participationId,
            offer_published_id: offerPublishedId,
            policy_number: offerDetail?.policy_number || "",
            message,
            published_by: brokerName || "",
          },
        },
      });
    } else {
      return await acceptOfferMutation({
        variables: {
          input: {
            offer_id: offer?.offer_id,
            offer_published_id: offerPublishedId,
            offer_participant_percentage: 0,
            policy_number: offerDetail?.policy_number || "",
            message,
            loyalty_status: false,
            published_by: brokerName || "",
          },
        },
      });
    }
  };

  const form = useFormik<MessageFormValues>({
    initialValues: {
      message: "",
    },
    onSubmit: async (values, { resetForm }) => {
      const plainTextMessage = values.message.replace(/<[^>]*>/g, "").trim();
      if (!plainTextMessage) return;

      try {
        const newMessage: MessageType = {
          uuid: `temp-${Date.now()}`,
          message: values.message,
          timestamp: new Date().toISOString(),
          sender_name: "You",
          state: "INTERNAL",
        };

        setMessages((prev) => [...prev, newMessage]);

        await handleMessageSubmission(values.message);

        resetForm();
        refetchMessages();
        hasMarkedAsRead.current = true;
      } catch (error) {
        console.error("Error sending message:", error);
      }
    },
  });

  const handleThreadUpdated = (eventName: string) => {
    refetchMessages();
  };

  useRealtimeConnection({
    subscriptions: [
      {
        channel: config.realtime.channels.KAVA_NOTIFICATION,
        events: [
          config.realtime.events.BROKER_READ_THREAD,
          config.realtime.events.BROKER_THREAD_NOTIFICATION,
        ],
      },
    ],
    callback: handleThreadUpdated,
  });
  const formatDate = (dateString?: string | null) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const isLoading = sendLoading || acceptLoading;

  const handleModalClose = () => {
    setOpen(false);
    if (hasMarkedAsRead.current && refetch) {
      refetch();
      hasMarkedAsRead.current = false;
    }
  };

  return (
    <Modal
      opened={open}
      onClose={handleModalClose}
      title="Transaction Thread"
      size="xl"
    >
      {/* Security Disclaimer */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4 mb-6">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg
              className="h-6 w-6 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
              />
            </svg>
          </div>
          <div className="ml-3">
            <h4 className="text-sm font-semibold text-blue-900 mb-2">
              Secure Transactional Threads
            </h4>
            <p className="text-sm text-blue-800 leading-relaxed">
              Secure Transactional Threads enable realtime, encrypted
              communication between communicating parties, ensuring confidential
              negotiations. When both parties are online, they can collaborate
              instantly—sharing documents, discussing terms, and closing deals
              faster. Key benefits include end-to-end security, live
              interaction, and streamlined workflows for time-sensitive
              transactions.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-md mb-4 border border-gray-200">
        <h4 className="font-medium text-gray-900 mb-3">Offer Details</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
          <p className="text-sm">
            <span className="text-gray-500">Business Name:</span>{" "}
            <span className="font-medium text-gray-800">
              {offer?.classofbusiness?.business_name || "N/A"}
            </span>
          </p>
          <p className="text-sm">
            <span className="text-gray-500">Insured:</span>{" "}
            <span className="font-medium text-gray-800">
              {offerDetail?.insured_by || "N/A"}
            </span>
          </p>
          <p className="text-sm">
            <span className="text-gray-500">Fac Offer:</span>{" "}
            <span className="font-medium text-gray-800">
              {offer?.facultative_offer || "N/A"}%
            </span>
          </p>
          <p className="text-sm">
            <span className="text-gray-500">Policy Number:</span>{" "}
            <span className="font-medium text-gray-800">
              {offerDetail?.policy_number || "N/A"}
            </span>
          </p>
          <p className="text-sm">
            <span className="text-gray-500">Period:</span>{" "}
            <span className="font-medium text-gray-800">
              {formatDate(offerDetail?.period_of_insurance_from)}
            </span>{" "}
            to{" "}
            <span className="font-medium text-gray-800">
              {formatDate(offerDetail?.period_of_insurance_to)}
            </span>
          </p>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-gray-500">Published By:</span>
            <PublisherPill publishedBy={brokerName} />
          </div>
          {offerPublished?.offer_published_participant && (
            <p className="text-sm md:col-span-2">
              <span className="text-gray-500">
                Your {isParticipationAccepted ? "Accepted" : "Proposed"}{" "}
                Interest:
              </span>{" "}
              <span
                className={clsx(
                  "font-medium",
                  isParticipationAccepted ? "text-green-600" : "text-red-600"
                )}
              >
                {offerPublished?.offer_published_participant
                  ?.offer_participant_percentage || "N/A"}
                %
              </span>
            </p>
          )}
        </div>
      </div>

      <div className="mt-2">
        <p className="text-sm text-gray-500">
          Communication thread with the reinsurer
        </p>
      </div>

      <div className="mt-5 mb-5">
        {fetchLoading ? (
          <div className="flex justify-center py-10">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-700"></div>
          </div>
        ) : (
          <EnhancedMessageView messages={messages} form={form} />
        )}
      </div>

      <div className="mt-6 flex justify-end space-x-2">
        <button
          type="button"
          onClick={form.submitForm}
          disabled={
            !form.values.message.replace(/<[^>]*>/g, "").trim() || isLoading
          }
          className="inline-flex items-center rounded-md border mx-1 border-transparent text-sm font-medium px-3 py-1.5 text-white shadow-sm bg-green-700 hover:bg-green-800 focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {isLoading ? (
            <>
              <span className="mr-2 animate-spin h-4 w-4 border-t-2 border-r-2 border-white rounded-full"></span>
              Sending...
            </>
          ) : (
            "Send Message"
          )}
        </button>
      </div>
    </Modal>
  );
};

export default ThreadContainer;
